import { z } from "zod";

import { audienceProfileSnapshotV1Schema } from "./audience-profile.ts";
import { brandSocialContentStyleSchema, brandSocialMechanicSchema } from "./brand-profile.ts";
import {
  getDefaultSocialPostPlatformForType,
  isSocialPostTypeSupportedByPlatform,
  socialPostPlatformSchema,
  socialPostTypeSchema,
  type SocialPostType,
} from "./social-post-scene.ts";

export const CAMPAIGN_GOAL_KEYS = [
  "brand_awareness",
  "product_education",
  "lead_generation",
  "sales_conversion",
  "retention",
  "event_promotion",
  "seasonal_push",
] as const;

export type CampaignGoalKey = (typeof CAMPAIGN_GOAL_KEYS)[number];
export const campaignGoalKeySchema = z.enum(CAMPAIGN_GOAL_KEYS);

export const campaignGoalSchema = z.object({
  key: z.union([campaignGoalKeySchema, z.literal("custom")]).default("custom"),
  label: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type CampaignGoal = z.infer<typeof campaignGoalSchema>;

export const campaignAudienceSegmentSchema = z.object({
  label: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type CampaignAudienceSegment = z.infer<typeof campaignAudienceSegmentSchema>;

export const campaignAddressedMarketSchema = z
  .object({
    scope: z.enum(["country", "region"]),
    countryCode: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .nullable()
      .default(null),
    regionCode: z
      .string()
      .regex(/^([0-9]{3}|[A-Z]{2,3})$/)
      .nullable()
      .default(null),
    label: z.string().trim().min(1),
    rationale: z.string().trim().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.scope === "country" && !value.countryCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Country addressed markets require countryCode.",
        path: ["countryCode"],
      });
    }

    if (value.scope === "country" && value.regionCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Country addressed markets must not include regionCode.",
        path: ["regionCode"],
      });
    }

    if (value.scope === "region" && !value.regionCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Region addressed markets require regionCode.",
        path: ["regionCode"],
      });
    }

    if (value.scope === "region" && value.countryCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Region addressed markets must not include countryCode.",
        path: ["countryCode"],
      });
    }
  });
export type CampaignAddressedMarket = z.infer<typeof campaignAddressedMarketSchema>;

export const campaignMessagingPillarSchema = z.object({
  label: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type CampaignMessagingPillar = z.infer<typeof campaignMessagingPillarSchema>;

export const CAMPAIGN_TYPES = [
  "launch",
  "awareness",
  "education",
  "consideration",
  "conversion",
  "retention",
  "seasonal",
  "evergreen",
] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];
export const campaignTypeSchema = z.enum(CAMPAIGN_TYPES);

export const CAMPAIGN_PRODUCTION_MODES = [
  "ai_generated_photo",
  "ai_generated_illustration",
  "branded_graphic",
] as const;
export type CampaignProductionMode = (typeof CAMPAIGN_PRODUCTION_MODES)[number];
export const campaignProductionModeSchema = z.enum(CAMPAIGN_PRODUCTION_MODES);

export const CAMPAIGN_REQUIRED_CAPABILITIES = [
  "static_image_generation",
  "brand_logo",
  "photo_references",
] as const;
export type CampaignRequiredCapability = (typeof CAMPAIGN_REQUIRED_CAPABILITIES)[number];
export const campaignRequiredCapabilitySchema = z.enum(CAMPAIGN_REQUIRED_CAPABILITIES);

export const CAMPAIGN_EXECUTION_FEASIBILITIES = ["supported", "unsupported"] as const;
export type CampaignExecutionFeasibility = (typeof CAMPAIGN_EXECUTION_FEASIBILITIES)[number];
export const campaignExecutionFeasibilitySchema = z.enum(CAMPAIGN_EXECUTION_FEASIBILITIES);
export const DEFAULT_CAMPAIGN_STRATEGY_RATIONALE =
  "Fit the campaign to the current brand context and audience maturity.";
export const DEFAULT_CAMPAIGN_VISUAL_APPROACH = "Photo-led editorial social creative.";
export const ON_DEMAND_CAMPAIGN_MAX_ITEMS = 10;
export const ON_DEMAND_CAMPAIGN_GENERATION_POST_TYPES = [
  "feed_portrait",
  "feed_square",
  "story",
  "carousel_portrait",
  "carousel_square",
] as const satisfies readonly SocialPostType[];
export type OnDemandCampaignGenerationPostType =
  (typeof ON_DEMAND_CAMPAIGN_GENERATION_POST_TYPES)[number];
export const onDemandCampaignGenerationPostTypeSchema = z.enum(
  ON_DEMAND_CAMPAIGN_GENERATION_POST_TYPES,
);
export const ON_DEMAND_CAMPAIGN_GENERATION_MODES = ["preview_first", "all_assets"] as const;
export type OnDemandCampaignGenerationMode = (typeof ON_DEMAND_CAMPAIGN_GENERATION_MODES)[number];
export const onDemandCampaignGenerationModeSchema = z.enum(ON_DEMAND_CAMPAIGN_GENERATION_MODES);

export const onDemandCampaignPromptOverridesV1Schema = z
  .object({
    audienceFocus: z
      .array(z.string().trim().min(1).max(120))
      .max(8)
      .default([])
      .describe("Audience segments explicitly requested in the user's campaign prompt."),
    audienceExclusions: z
      .array(z.string().trim().min(1).max(120))
      .max(8)
      .default([])
      .describe("Audience segments explicitly excluded by the user's campaign prompt."),
    directions: z
      .array(z.string().trim().min(1).max(280))
      .max(16)
      .default([])
      .describe(
        "Other explicit user-provided campaign directions, such as goal, offer, CTA, tone, market, occasion, messaging angle, or exclusions.",
      ),
  })
  .strict();
export type OnDemandCampaignPromptOverridesV1 = z.infer<
  typeof onDemandCampaignPromptOverridesV1Schema
>;

export const campaignSocialCreativeBriefV1Schema = z
  .object({
    version: z.literal(1),
    style: brandSocialContentStyleSchema.describe(
      "Brand social content style selected for this specific post.",
    ),
    mechanic: brandSocialMechanicSchema.describe(
      "Platform-native social mechanic selected for this specific post.",
    ),
    tension: z
      .string()
      .trim()
      .min(1)
      .max(280)
      .describe("Audience tension, contradiction, or hook pressure this post should dramatize."),
  })
  .strict();
export type CampaignSocialCreativeBriefV1 = z.infer<typeof campaignSocialCreativeBriefV1Schema>;

export const campaignSocialCreativeBriefV2Schema = z
  .object({
    version: z.literal(2),
    contentDnaStyle: brandSocialContentStyleSchema.describe(
      "Content DNA style selected for this specific post.",
    ),
    platformNativeMechanic: brandSocialMechanicSchema.describe(
      "Primary platform-native mechanic selected for this specific post.",
    ),
    supportingMechanic: brandSocialMechanicSchema
      .nullable()
      .describe("Optional supporting mechanic for variety or structure."),
    scrollStoppingTension: z
      .string()
      .trim()
      .min(1)
      .max(320)
      .describe(
        "Human tension, awkward moment, contradiction, mistake, desire, frustration, social truth, or relatable observation that makes the post worth stopping for.",
      ),
    whySomeoneWouldComment: z
      .string()
      .trim()
      .min(1)
      .max(320)
      .describe("Why the audience would tag, disagree, laugh, share, or say this is me."),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.supportingMechanic === value.platformNativeMechanic) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["supportingMechanic"],
        message: "Supporting mechanic must differ from the primary mechanic.",
      });
    }
  });
export type CampaignSocialCreativeBriefV2 = z.infer<typeof campaignSocialCreativeBriefV2Schema>;

export const campaignSocialCreativeBriefSchema = z.union([
  campaignSocialCreativeBriefV2Schema,
  campaignSocialCreativeBriefV1Schema,
]);
export type CampaignSocialCreativeBrief = z.infer<typeof campaignSocialCreativeBriefSchema>;

export const normalizeCampaignSocialCreativeBrief = (
  input: unknown,
): CampaignSocialCreativeBriefV2 | undefined => {
  const v2 = campaignSocialCreativeBriefV2Schema.safeParse(input);
  if (v2.success) {
    return v2.data;
  }

  const v1 = campaignSocialCreativeBriefV1Schema.safeParse(input);
  if (!v1.success) {
    return undefined;
  }

  return {
    version: 2,
    contentDnaStyle: v1.data.style,
    platformNativeMechanic: v1.data.mechanic,
    supportingMechanic: null,
    scrollStoppingTension: v1.data.tension,
    whySomeoneWouldComment: "The audience recognizes the tension and can respond from experience.",
  };
};

export const campaignBriefV1Schema = z.object({
  version: z.literal(1),
  campaignType: campaignTypeSchema.default("evergreen"),
  summary: z.string().trim().min(1),
  strategyRationale: z.string().trim().min(1).default(DEFAULT_CAMPAIGN_STRATEGY_RATIONALE),
  goals: z.array(campaignGoalSchema).min(1),
  audienceSegments: z.array(campaignAudienceSegmentSchema).min(1),
  addressedMarkets: z.array(campaignAddressedMarketSchema).default([]),
  messagingPillars: z.array(campaignMessagingPillarSchema).default([]),
  callToActions: z.array(z.string().trim().min(1)).default([]),
});
export type CampaignBriefV1 = z.infer<typeof campaignBriefV1Schema>;

export const campaignContentSlotV1Schema = z
  .object({
    version: z.literal(1),
    concept: z.string().trim().min(1),
    objective: z.union([campaignGoalKeySchema, z.literal("custom")]).default("custom"),
    objectiveLabel: z.string().trim().min(1).optional(),
    audienceSegment: z.string().trim().min(1).nullable().default(null),
    callToAction: z.string().trim().min(1).nullable().default(null),
    visualApproach: z.string().trim().min(1).default(DEFAULT_CAMPAIGN_VISUAL_APPROACH),
    productionMode: campaignProductionModeSchema.default("ai_generated_photo"),
    requiredCapabilities: z
      .array(campaignRequiredCapabilitySchema)
      .default(["static_image_generation"]),
    requiresSpecificPersonLikeness: z.boolean().default(false),
    requiresVideo: z.boolean().default(false),
    executionFeasibility: campaignExecutionFeasibilitySchema.default("supported"),
    platform: socialPostPlatformSchema.default("instagram"),
    postType: socialPostTypeSchema.default("feed_portrait"),
    socialCreativeBrief: campaignSocialCreativeBriefSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (!isSocialPostTypeSupportedByPlatform(value.platform, value.postType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["postType"],
        message: "Unsupported post type for platform.",
      });
    }
  });
export type CampaignContentSlotV1 = z.infer<typeof campaignContentSlotV1Schema>;

export const campaignScheduleItemSchema = z
  .object({
    time: z.string().datetime(),
    platform: socialPostPlatformSchema.default("instagram"),
    postType: socialPostTypeSchema.default("feed_portrait"),
    slot: campaignContentSlotV1Schema,
  })
  .superRefine((value, ctx) => {
    if (!isSocialPostTypeSupportedByPlatform(value.platform, value.postType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["postType"],
        message: "Unsupported post type for platform.",
      });
    }
  });

export const campaignScheduleSchema = z.object({
  items: z.array(campaignScheduleItemSchema).default([]),
});

export type CampaignScheduleItem = z.infer<typeof campaignScheduleItemSchema>;
export type CampaignSchedule = z.infer<typeof campaignScheduleSchema>;

export const onDemandCampaignScheduleItemV1Schema = z
  .object({
    scheduledFor: z.string().datetime(),
    platform: socialPostPlatformSchema.default("instagram"),
    postType: socialPostTypeSchema.default("feed_portrait"),
  })
  .superRefine((value, ctx) => {
    if (!isSocialPostTypeSupportedByPlatform(value.platform, value.postType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["postType"],
        message: "Unsupported post type for platform.",
      });
    }
  });
export type OnDemandCampaignScheduleItemV1 = z.infer<typeof onDemandCampaignScheduleItemV1Schema>;

export const onDemandCampaignCreateRequestV1Schema = z.object({
  version: z.literal(1),
  prompt: z.string().trim().min(1).max(4_000),
  promptOverrides: onDemandCampaignPromptOverridesV1Schema.optional(),
  items: z.array(onDemandCampaignScheduleItemV1Schema).min(1).max(ON_DEMAND_CAMPAIGN_MAX_ITEMS),
  generationMode: onDemandCampaignGenerationModeSchema.default("preview_first"),
});
export type OnDemandCampaignCreateRequestV1 = z.infer<typeof onDemandCampaignCreateRequestV1Schema>;

export const campaignGenerationAutopilotJobPayloadV1Schema = z.object({
  version: z.literal(1),
  mode: z.literal("autopilot"),
  audienceProfile: audienceProfileSnapshotV1Schema.optional(),
});
export type CampaignGenerationAutopilotJobPayloadV1 = z.infer<
  typeof campaignGenerationAutopilotJobPayloadV1Schema
>;

export const campaignGenerationOnDemandJobPayloadV1Schema = z.object({
  version: z.literal(1),
  mode: z.literal("on_demand"),
  request: onDemandCampaignCreateRequestV1Schema,
  sourceBrandAssistantChatId: z.string().uuid().nullable().optional(),
  sourceBrandAssistantProposalId: z.string().trim().min(1).max(160).nullable().optional(),
  campaignPlannerRunsUsed: z.number().int().nonnegative().default(0),
  generationMode: onDemandCampaignGenerationModeSchema.default("preview_first"),
  audienceProfile: audienceProfileSnapshotV1Schema.optional(),
});
export type CampaignGenerationOnDemandJobPayloadV1 = z.infer<
  typeof campaignGenerationOnDemandJobPayloadV1Schema
>;

export const campaignGenerationJobPayloadV1Schema = z.union([
  campaignGenerationAutopilotJobPayloadV1Schema,
  campaignGenerationOnDemandJobPayloadV1Schema,
]);
export type CampaignGenerationJobPayloadV1 = z.infer<typeof campaignGenerationJobPayloadV1Schema>;

export const normalizeCampaignGenerationJobPayload = (
  input: unknown,
): CampaignGenerationJobPayloadV1 => {
  const parsed = campaignGenerationJobPayloadV1Schema.safeParse(input);
  if (parsed.success) {
    return parsed.data;
  }

  const legacyPayload = z.object({}).strict().safeParse(input);
  if (legacyPayload.success) {
    return { version: 1, mode: "autopilot" };
  }

  return campaignGenerationJobPayloadV1Schema.parse(input);
};

export const validateOnDemandCampaignCreateRequest = (
  input: unknown,
): OnDemandCampaignCreateRequestV1 => onDemandCampaignCreateRequestV1Schema.parse(input);

export const CAMPAIGN_STATUSES = [
  "draft",
  "pending_review",
  "approved",
  "active",
  "paused",
  "completed",
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const campaignStatusSchema = z.enum(CAMPAIGN_STATUSES);

export const SCHEDULED_POST_STATUSES = [
  "draft",
  "scheduled",
  "generating",
  "generated",
  "failed",
] as const;
export type ScheduledPostStatus = (typeof SCHEDULED_POST_STATUSES)[number];

export const scheduledPostStatusSchema = z.enum(SCHEDULED_POST_STATUSES);

export const SCHEDULED_POST_APPROVAL_STATUSES = ["pending", "approved", "rejected"] as const;
export type ScheduledPostApprovalStatus = (typeof SCHEDULED_POST_APPROVAL_STATUSES)[number];

export const scheduledPostApprovalStatusSchema = z.enum(SCHEDULED_POST_APPROVAL_STATUSES);

const splitLegacyStructuredText = (value: string | null | undefined): string[] => {
  if (!value) {
    return [];
  }

  const normalized = value
    .split(/\r?\n|•|\u2022|;/u)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  if (normalized.length > 0) {
    return normalized;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? [trimmed] : [];
};

const toFallbackGoal = (value: string): CampaignGoal => ({
  key: "custom",
  label: value,
});

const toFallbackAudienceSegment = (value: string): CampaignAudienceSegment => ({
  label: value,
});

export const summarizeCampaignGoals = (goals: CampaignGoal[]): string =>
  goals.map((goal) => goal.label).join("; ");

export const summarizeCampaignAudienceSegments = (
  audienceSegments: CampaignAudienceSegment[],
): string => audienceSegments.map((segment) => segment.label).join("; ");

export const normalizeCampaignBrief = (input: {
  briefJson?: unknown;
  goals?: string | null;
  audience?: string | null;
}): CampaignBriefV1 => {
  const parsed = campaignBriefV1Schema.safeParse(input.briefJson);
  if (parsed.success) {
    return parsed.data;
  }

  const fallbackGoals = splitLegacyStructuredText(input.goals).map(toFallbackGoal);
  const fallbackAudience = splitLegacyStructuredText(input.audience).map(toFallbackAudienceSegment);

  return {
    version: 1,
    campaignType: "evergreen",
    summary: input.goals?.trim() || input.audience?.trim() || "Generated marketing campaign brief.",
    strategyRationale: DEFAULT_CAMPAIGN_STRATEGY_RATIONALE,
    goals: fallbackGoals.length > 0 ? fallbackGoals : [toFallbackGoal("General campaign support")],
    audienceSegments:
      fallbackAudience.length > 0
        ? fallbackAudience
        : [toFallbackAudienceSegment("General brand audience")],
    addressedMarkets: [],
    messagingPillars: [],
    callToActions: [],
  };
};

export const normalizeCampaignContentSlot = (input: {
  slotJson?: unknown;
  fallbackConcept?: string | null;
  fallbackPlatform?: z.infer<typeof socialPostPlatformSchema> | null;
  fallbackPostType?: z.infer<typeof socialPostTypeSchema> | null;
}): CampaignContentSlotV1 => {
  const parsed = campaignContentSlotV1Schema.safeParse(input.slotJson);
  if (parsed.success) {
    return parsed.data;
  }

  if (typeof input.slotJson === "string") {
    try {
      const parsedString = campaignContentSlotV1Schema.safeParse(JSON.parse(input.slotJson));
      if (parsedString.success) {
        return parsedString.data;
      }
    } catch {
      // Fall through to the legacy fallback below.
    }
  }

  const fallbackPostType = input.fallbackPostType ?? "feed_portrait";
  const fallbackPlatform =
    input.fallbackPlatform &&
    isSocialPostTypeSupportedByPlatform(input.fallbackPlatform, fallbackPostType)
      ? input.fallbackPlatform
      : (getDefaultSocialPostPlatformForType(fallbackPostType) ?? "instagram");

  return {
    version: 1,
    concept: input.fallbackConcept?.trim() || "Planned social post",
    objective: "custom",
    audienceSegment: null,
    callToAction: null,
    visualApproach: DEFAULT_CAMPAIGN_VISUAL_APPROACH,
    productionMode: "ai_generated_photo",
    requiredCapabilities: ["static_image_generation"],
    requiresSpecificPersonLikeness: false,
    requiresVideo: false,
    executionFeasibility: "supported",
    platform: fallbackPlatform,
    postType: fallbackPostType,
  };
};
